import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config/api';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import BookingConfirmationModal from './BookingConfirmationModal';

interface AmenitiesBookingModalProps {
  show: boolean;
  onHide: () => void;
  amenity: any;
  hotelId: string;
  guestId: string;
  onBookingSuccess: (booking: any) => void;
}

function getDayOfWeek(dateStr: string) {
  // 0=Sunday, 6=Saturday
  return new Date(dateStr).getDay();
}

const AmenitiesBookingModal: React.FC<AmenitiesBookingModalProps> = ({ show, onHide, amenity, hotelId, guestId, onBookingSuccess }) => {
  const [slots, setSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [specialRequest, setSpecialRequest] = useState('');
  const [booking, setBooking] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingGuestName, setPendingGuestName] = useState('');

  useEffect(() => {
    if (show && amenity && hotelId) {
      setLoading(true);
      setError('');
      setSlots([]);
      setSelectedSlot(null);
      setSpecialRequest('');
      setSuccess(false);
      setSuccessMsg('');
      setSelectedDate('');
      fetch(API_ENDPOINTS.AMENITIES_SLOTS(hotelId, amenity.id))
        .then(res => res.json())
        .then(data => {
          setSlots(Array.isArray(data) ? data : []);
          setLoading(false);
        })
        .catch(() => {
          setError('Failed to load slots. Please try again.');
          setLoading(false);
        });
    }
  }, [show, amenity, hotelId]);

  // Filter slots by selected date's day of week
  let filteredSlots: any[] = [];
  if (selectedDate && slots.length > 0) {
    const day = getDayOfWeek(selectedDate);
    filteredSlots = slots.filter(slot =>
      slot.day_of_week === null || slot.day_of_week === '' || slot.day_of_week === undefined || String(slot.day_of_week) === String(day)
    );
  }

  // Get guest name from localStorage or guest context if available
  React.useEffect(() => {
    if (show) {
      const stored = localStorage.getItem('guestSession');
      let name = '';
      if (stored) {
        try { name = JSON.parse(stored).name || ''; } catch {}
      }
      setPendingGuestName(name);
    }
  }, [show]);

  const handleBook = () => {
    setShowConfirm(true);
  };

  const handleConfirmBooking = async () => {
    setShowConfirm(false);
    setBooking(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.AMENITIES_BOOK(hotelId, amenity.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slot_id: selectedSlot.id,
          guest_id: guestId,
          booking_date: selectedDate,
          special_request: specialRequest,
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Booking failed.');
        setBooking(false);
        return;
      }
      setSuccess(true);
      setSuccessMsg('Booking successful! We look forward to welcoming you.');
      setBooking(false);
      onBookingSuccess(data);
      setTimeout(() => {
        onHide();
      }, 1500);
    } catch (err) {
      setError('Network error. Please try again.');
      setBooking(false);
    }
  };

  // Get today's date in yyyy-mm-dd
  const today = new Date();
  const minDate = today.toISOString().slice(0, 10);

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Book {amenity?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4"><span className="spinner-border" /> Loading slots...</div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : success ? (
          <div className="alert alert-success text-center py-4">
            <i className="fas fa-check-circle fa-2x mb-2 text-success"></i><br />
            {successMsg}
          </div>
        ) : (
          <>
            <div className="mb-3">
              <strong>Select a date:</strong>
              <input
                type="date"
                className="form-control mt-2"
                min={minDate}
                value={selectedDate}
                onChange={e => {
                  setSelectedDate(e.target.value);
                  setSelectedSlot(null);
                }}
                disabled={booking}
                onKeyDown={e => e.preventDefault()}
                style={{ cursor: 'pointer', backgroundColor: '#fff' }}
              />
            </div>
            {selectedDate && (
              <div className="mb-3">
                <strong>Available slots for {selectedDate}:</strong>
                <div className="list-group mt-2">
                  {filteredSlots.length === 0 && <div className="text-muted">No slots available for this date.</div>}
                  {filteredSlots.map(slot => (
                    <button
                      key={slot.id}
                      className={`list-group-item list-group-item-action${selectedSlot && selectedSlot.id === slot.id ? ' active' : ''}`}
                      onClick={() => setSelectedSlot(slot)}
                      disabled={booking}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <span><i className="fas fa-clock me-2"></i>{slot.start_time} - {slot.end_time}</span>
                        <span className="badge bg-primary">₹{slot.price}</span>
                      </div>
                      <div className="small text-muted">Capacity: {slot.slot_capacity}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">Special Request (optional)</label>
              <input
                type="text"
                className="form-control"
                value={specialRequest}
                onChange={e => setSpecialRequest(e.target.value)}
                disabled={booking}
                maxLength={200}
                placeholder="e.g. Need wheelchair access, etc."
              />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!success && (
          <Button variant="secondary" onClick={onHide} disabled={booking}>Cancel</Button>
        )}
        {!success && (
          <Button variant="primary" onClick={handleBook} disabled={!selectedSlot || !selectedDate || booking}>
            <i className="fas fa-calendar-check me-2" />Book Now
          </Button>
        )}
      </Modal.Footer>
      {selectedSlot && (
        <BookingConfirmationModal
          show={showConfirm}
          onHide={() => setShowConfirm(false)}
          onConfirm={handleConfirmBooking}
          bookingDetails={{
            amenityName: amenity?.name,
            date: selectedDate,
            slot: selectedSlot,
            price: selectedSlot?.price,
            specialRequest: specialRequest,
          }}
        />
      )}
    </Modal>
  );
};

export default AmenitiesBookingModal; 